/**
 * The 18 views §2 requires, each declaring what it can actually measure today.
 *
 * `coverage` is not cosmetic. It drives the "no data" marker in the nav, so you learn a view
 * is blind before you click it rather than after — and it is derived from what is genuinely
 * scraped, verified against the live Prometheus on 2026-09-22, not from what we wish existed.
 */

import { Reading } from './Reading';
import { MyNotifications } from './views/MyNotifications';
import { Alerts } from './views/Alerts';
import { DeliveryHistory, Integrations, Routing } from './views/Console';
import { DatabaseLive, InfrastructureLive, JobsLive, MetricReading, SecurityLive, TrafficLive, VaprLive, VideoLive } from './views/Live';
import { Account, Overview } from './views/Summary';
import { LoadTests } from './views/LoadTests';
import { Capacity } from './views/Capacity';
import { VIEWS as META, type Coverage, type ViewMeta } from './views-meta';

export type { Coverage };

export interface ViewDef extends ViewMeta {
  blurb: string;
  render: () => JSX.Element;
}

/** id + blurb + render here; title, group and coverage come from views-meta. */
interface ViewBody { id: string; blurb: string; render: () => JSX.Element }

const BODIES: ViewBody[] = [
  {
    id: 'overview',
    blurb: 'What is measured right now, and what is not.',
    render: () => <Overview />,
  },
  {
    id: 'traffic',
    blurb: 'HTTP load from the ingress, viewers from playback telemetry.',
    render: () => <TrafficLive />,
  },
  {
    id: 'video',
    blurb: 'Viewers and playback problems are measured. Start time is not.',
    render: () => <VideoLive />,
  },
  {
    id: 'infra',
    blurb: 'Workloads, node headroom and Kubernetes health.',
    render: () => <InfrastructureLive />,
  },
  {
    id: 'database',
    blurb: 'PostgreSQL and Redis, measured live.',
    render: () => <DatabaseLive />,
  },
  {
    id: 'jobs',
    blurb: 'Transcoding, imports and classification queues.',
    render: () => <JobsLive />,
  },
  {
    id: 'vapr',
    blurb: 'Ad selection, match rate and delivery.',
    render: () => <VaprLive />,
  },
  {
    id: 'alerts',
    blurb: 'What is firing, and what was done about it.',
    render: () => <Alerts />,
  },
  {
    id: 'capacity',
    blurb: 'What the platform can carry.',
    render: () => <Capacity />,
  },
  {
    id: 'tests',
    blurb: 'Planned, bounded, approved load generation.',
    render: () => <LoadTests />,
  },
  {
    id: 'costs',
    blurb: 'Volume capacity, and video storage cost at list price.',
    render: () => <Costs />,
  },
  {
    id: 'security',
    blurb: 'Backups, restores and recovery readiness.',
    render: () => <SecurityLive />,
  },
  {
    id: 'runbooks',
    blurb: 'Written procedures, kept where they already live.',
    render: () => (
      <div className="notice">
        <h3>Runbooks live in greyhive/ops-runbooks</h3>
        <p>
          They are not copied here. A second copy of a runbook is a copy that goes stale, and
          the stale one is the one someone reads at 3am. Alert annotations link straight to the
          repository.
        </p>
      </div>
    ),
  },
  {
    id: 'integrations',
    blurb: 'Connections, their last success, and their last failure.',
    render: () => <Integrations />,
  },
  {
    id: 'notifications',
    blurb: 'When this account is mentioned in the shared channel.',
    render: () => <MyNotifications />,
  },
  {
    id: 'routing',
    blurb: 'Where operational messages are sent.',
    render: () => <Routing />,
  },
  {
    id: 'delivery',
    blurb: 'Every attempt, its outcome, and why it failed.',
    render: () => <DeliveryHistory />,
  },
  {
    id: 'account',
    blurb: 'This session and the access behind it.',
    render: () => <Account />,
  },
];

/* ------------------------------------------------------------------ views */




function Costs() {
  return (
    <>
      <div className="readings">
        {/* Was the literal string "measured" wearing a live badge. The metric is real and
            already in the catalog, so it reads the actual fullest volume. */}
        <MetricReading id="volume.used" label="Fullest volume" reduce="peak"
          suffixFromLabel="persistentvolumeclaim" />
        <MetricReading id="cost.videoStorageMonthly" label="Video storage, at list price" reduce="single" />
        <Reading label="Video egress spend" value="$0" unit="/month" state="unverified" source="American Cloud pricing"
          reason="American Cloud charges no egress or request fees on object storage (published pricing, checked 2026-10-04). Not read from a bill." />
      </div>
      <div className="notice">
        <h3>Prices are published rates, not a bill</h3>
        <p>
          Video storage is PeerTube's own measured bytes at American Cloud's list price of
          $0.02 per GB-month. The bucket also holds staging copies and 30 days of old versions,
          so the bill is higher (9.7 TB in the bucket on 27 September versus PeerTube's own count).
          Compute, volumes and IPs are not read from billing.
        </p>
      </div>
    </>
  );
}





/**
 * One definition per view, assembled from the two halves. A body without metadata, or metadata
 * without a body, throws at module load rather than rendering a view with a missing title or
 * silently dropping one from the nav.
 */
export const VIEWS: ViewDef[] = META.map((m) => {
  const body = BODIES.find((b) => b.id === m.id);
  if (!body) throw new Error(`views-meta lists "${m.id}" but views.tsx has no body for it`);
  return { ...m, blurb: body.blurb, render: body.render };
});

for (const b of BODIES) {
  if (!META.some((m) => m.id === b.id)) {
    throw new Error(`views.tsx defines "${b.id}" but views-meta does not list it`);
  }
}
